import groupBy from 'lodash/groupBy';
import mapValues from 'lodash/mapValues';

import { FindingStatusSortRank } from './constants';
import type { ScanObjectMap } from './types';
import { RemedyObjectAccess, RemedyObjectType } from './types';

import type { EditorDiagnosticType } from '@/features/code-editor/types';
import type {
  TLAIRemedyV3FindingResponse,
  TLAIRemedyV3ObjectDetail,
  TLAIRemedyV3ScanInfo,
  TLAIUserInfo,
} from '@/types/api';
import { TLAIMessageType, TLAIRemedyObjectStatus, TLAIRemedyScanStatus, TLAIRemedyV3Priority } from '@/types/api';
import { compareNumbers } from '@/utils/sort';

/**
 * Regex pattern to match SAP class method implementation objects.
 * Matches names ending with CM followed by 3 alphanumeric characters (e.g., CM001, CMABC, CM1A2)
 */
export const CLASS_METHOD_PATTERN = /CM[A-Z0-9]{3}$/i;

export function isClassMethod(objectName: string): boolean {
  return CLASS_METHOD_PATTERN.test(objectName);
}

export function computeIssueLineNumber(
  finding: TLAIRemedyV3FindingResponse,
  object: TLAIRemedyV3ObjectDetail,
  code: string,
) {
  if (!finding.line) return 0;
  if (object.object_type !== RemedyObjectType.CLASS) return finding.line;
  if (!finding.subobject?.length) return finding.line;

  const lines = code.split('\n');
  const methodString = `METHOD ${finding.subobject.toLowerCase()}`.toLowerCase();

  const methodLine = lines.findIndex((line) => line.toLowerCase().includes(methodString));
  if (methodLine === -1) return finding.line;

  return methodLine + finding.line;
}

export function orderObjects(objects: TLAIRemedyV3ObjectDetail[], parentObjectId: string) {
  return [...objects].sort((a, b) => {
    if (a.object_id === parentObjectId) {
      return -1;
    }
    if (b.object_id === parentObjectId) {
      return 1;
    }
    return 0;
  });
}

export function compareObjects(a: TLAIRemedyV3ObjectDetail, b: TLAIRemedyV3ObjectDetail) {
  if (!a.parent_id && !b.parent_id) {
    return a.name.localeCompare(b.name);
  }
  if (!a.parent_id) {
    return -1;
  }
  if (!b.parent_id) {
    return 1;
  }
  return a.name.localeCompare(b.name);
}

export function compareScans(a: TLAIRemedyV3ScanInfo, b: TLAIRemedyV3ScanInfo) {
  return new Date(b.created_date).getTime() - new Date(a.created_date).getTime();
}

/**
 * Orders findings by `FindingStatusSortRank` first, then by line number ascending.
 * A status the frontend doesn't recognize sorts after every known bucket; within a
 * bucket, findings with no line sort last.
 */
export function compareFindings(a: TLAIRemedyV3FindingResponse, b: TLAIRemedyV3FindingResponse) {
  const byStatus = compareNumbers(
    FindingStatusSortRank[a.status] ?? Number.POSITIVE_INFINITY,
    FindingStatusSortRank[b.status] ?? Number.POSITIVE_INFINITY,
  );
  if (byStatus !== 0) return byStatus;

  return compareNumbers(a.line ?? Number.POSITIVE_INFINITY, b.line ?? Number.POSITIVE_INFINITY);
}

/**
 * Builds a comparator that lifts objects with syntax issues or findings to the top of
 * whatever list they're sorted in, leaving same-ranked objects in their existing order
 * (`Array#sort` is stable). The parent object stays pinned first even when it's clean —
 * it anchors the page, so demoting it below its own sub-objects would be disorienting.
 *
 * @param parentObjectId - Object id to pin ahead of everything else.
 * @param hasIssues - Whether the given object has syntax issues or findings.
 * @returns A comparator for `TLAIRemedyV3ObjectDetail`.
 *
 * @example
 * objects.sort(createObjectIssueComparator(parentId, (id) => issueIds.has(id)))
 */
export function createObjectIssueComparator(parentObjectId: string, hasIssues: (objectId: string) => boolean) {
  const rank = (objectId: string) => {
    if (objectId === parentObjectId) return 0;
    return hasIssues(objectId) ? 1 : 2;
  };

  return (a: TLAIRemedyV3ObjectDetail, b: TLAIRemedyV3ObjectDetail) =>
    compareNumbers(rank(a.object_id), rank(b.object_id));
}

export function isScanActuallyInProgress(object_id: string, scans?: TLAIRemedyV3ScanInfo[]): boolean {
  const objectScans = scans?.filter((scan) => scan.object_id === object_id)?.sort(compareScans);
  if (!objectScans?.length) {
    return false;
  }

  const latestScan = objectScans[0];
  return latestScan.status === TLAIRemedyScanStatus.IN_PROGRESS;
}

export function isScanCompleted(scan: TLAIRemedyV3ScanInfo): boolean {
  return scan.status === TLAIRemedyScanStatus.COMPLETED;
}

export function buildScanObjectMap(scans: TLAIRemedyV3ScanInfo[]): ScanObjectMap {
  return mapValues(groupBy(scans, 'object_id'), (s) => s.sort(compareScans));
}

export function mapPriorityToDiagnosticType(priority: TLAIRemedyV3Priority): EditorDiagnosticType {
  switch (priority) {
    case TLAIRemedyV3Priority.HIGH:
      return 'error';
    case TLAIRemedyV3Priority.MEDIUM:
      return 'warning';
    case TLAIRemedyV3Priority.LOW:
      return 'info';
    default:
      return 'info';
  }
}

export function mapSyntaxMessageTypeToDiagnosticType(syntaxType: TLAIMessageType): EditorDiagnosticType {
  switch (syntaxType) {
    case TLAIMessageType.ERROR:
      return 'error';
    case TLAIMessageType.WARNING:
      return 'warning';
    case TLAIMessageType.INFO:
      return 'info';
    case TLAIMessageType.UNKNOWN:
      return 'info';
    default:
      return 'info';
  }
}

export function getObjectAccess(object: TLAIRemedyV3ObjectDetail, user: TLAIUserInfo) {
  if (!user.email) return RemedyObjectAccess.UNKNOWN_USER;
  if (
    (object.assigned_to === user.email && object.status === TLAIRemedyObjectStatus.READY_FOR_REVIEW) ||
    (object.assigned_to !== user.email &&
      object.assigned_by === user.email &&
      object.status !== TLAIRemedyObjectStatus.READY_FOR_REVIEW) ||
    object.status === TLAIRemedyObjectStatus.COMPLETED
  )
    return RemedyObjectAccess.UNMODIFIABLE_STATUS;

  if (
    (object.assigned_by === user.email && object.status === TLAIRemedyObjectStatus.READY_FOR_REVIEW) ||
    object.assigned_to === user.email
  )
    return RemedyObjectAccess.READ_WRITE;

  return RemedyObjectAccess.NOT_ASSIGNED;
}

export function messageTypeNumericValue(messageType: TLAIMessageType): number {
  switch (messageType) {
    case TLAIMessageType.ERROR:
      return 0;
    case TLAIMessageType.WARNING:
      return 1;
    case TLAIMessageType.INFO:
      return 2;
    default:
      return -1;
  }
}
